import React, { useState } from 'react';
import { Image } from '../Image/Image';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './BrandLogosBanner.scss';

/**
 * BrandLogosBanner — horizontal strip of brand / partner logos.
 *
 * Renders a single row of logo images at a fixed height with object-fit
 * contain so heterogeneous logo shapes (wordmarks, symbols, wide vs square)
 * all read at the same visual height.
 *
 * Layout depends on how many logo slots are populated:
 * - 3 to 5 logos: a static row, centered in the standard container.
 * - 6 to 12 logos: a marquee. The logos repeat until each half of the track
 *   holds at least 12 items, so a short set still spans wide viewports, then
 *   the half is cloned and animated translateX(0) → -50% on a seamless
 *   infinite loop. Every repeat is aria-hidden and decorative. Motion pauses
 *   on hover / keyboard focus and via a focusable pause/play control (so
 *   keyboard and touch users can stop it too), and disables entirely under
 *   prefers-reduced-motion: reduce (the row falls back to the static layout).
 *
 * Each logo may be decorative or linked:
 * - `logo{N}Alt` sets the accessible name. Omit it for a purely decorative
 *   logo (renders alt=""); a dev warning flags the missing alt (WCAG 1.1.1).
 * - `logo{N}Href` wraps the logo in an anchor and `logo{N}CtaId` is emitted
 *   as `data-cta-id` for click tracking. A linked logo with no alt would
 *   produce an anchor with no accessible name, so the link is dropped and a
 *   dev warning fires (WCAG 2.4.4) — the image still renders.
 *
 * Clones are never linked and always decorative so screen readers announce
 * each brand exactly once.
 *
 * Requires **at least 3 populated logo slots**; fewer yields a
 * `data-poly-error` placeholder + dev `console.warn`. Up to 12 slots are
 * supported.
 */

/** Minimum populated logo slots required for the banner to render. */
export const BRAND_LOGOS_BANNER_MIN_LOGOS = 3;

/**
 * Populated logo count at which the banner switches from a static row to the
 * marquee. Six logos at the maximum logo width overflow the 1216px container,
 * so a static row would wrap onto a second line.
 */
export const BRAND_LOGOS_BANNER_MIN_SCROLLING_LOGOS = 6;

/** Maximum authorable logo slots (matches the flat-prop cap below). */
export const BRAND_LOGOS_BANNER_MAX_LOGOS = 12;

/**
 * Flat, per-slot props for logos 1-12. Slots 1-3 are required so the CMS
 * refuses to save a banner with fewer than 3 logos.
 * Each slot has an image URL plus optional alt text, link href, and ctaId.
 */
export interface BrandLogosBannerSlotProps {
  /** Logo 1 */
  /**
   * @polyType Url
   * @polyHelp Required. A logo banner needs at least 3 logos and holds up to 12. 3 to 5 logos sit still in a row; 6 or more scroll.
   */
  logo1ImageUrl: string;
  /** @polyTranslatable true */
  logo1Alt?: string;
  /** @polyType Url */
  logo1Href?: string;
  /** @polyTranslatable false */
  logo1CtaId?: string;

  /** Logo 2 */
  /**
   * @polyType Url
   * @polyHelp Required. A logo banner needs at least 3 logos and holds up to 12. 3 to 5 logos sit still in a row; 6 or more scroll.
   */
  logo2ImageUrl: string;
  /** @polyTranslatable true */
  logo2Alt?: string;
  /** @polyType Url */
  logo2Href?: string;
  /** @polyTranslatable false */
  logo2CtaId?: string;

  /** Logo 3 */
  /**
   * @polyType Url
   * @polyHelp Required. A logo banner needs at least 3 logos and holds up to 12. 3 to 5 logos sit still in a row; 6 or more scroll.
   */
  logo3ImageUrl: string;
  /** @polyTranslatable true */
  logo3Alt?: string;
  /** @polyType Url */
  logo3Href?: string;
  /** @polyTranslatable false */
  logo3CtaId?: string;

  /** Logo 4 */
  /**
   * @polyType Url
   * @polyHelp Optional. 3 to 5 logos sit still in a row; adding a 6th makes the banner scroll. Up to 12 logos.
   */
  logo4ImageUrl?: string;
  /** @polyTranslatable true */
  logo4Alt?: string;
  /** @polyType Url */
  logo4Href?: string;
  /** @polyTranslatable false */
  logo4CtaId?: string;

  /** Logo 5 */
  /** @polyType Url */
  logo5ImageUrl?: string;
  /** @polyTranslatable true */
  logo5Alt?: string;
  /** @polyType Url */
  logo5Href?: string;
  /** @polyTranslatable false */
  logo5CtaId?: string;

  /** Logo 6 */
  /** @polyType Url */
  logo6ImageUrl?: string;
  /** @polyTranslatable true */
  logo6Alt?: string;
  /** @polyType Url */
  logo6Href?: string;
  /** @polyTranslatable false */
  logo6CtaId?: string;

  /** Logo 7 */
  /** @polyType Url */
  logo7ImageUrl?: string;
  /** @polyTranslatable true */
  logo7Alt?: string;
  /** @polyType Url */
  logo7Href?: string;
  /** @polyTranslatable false */
  logo7CtaId?: string;

  /** Logo 8 */
  /** @polyType Url */
  logo8ImageUrl?: string;
  /** @polyTranslatable true */
  logo8Alt?: string;
  /** @polyType Url */
  logo8Href?: string;
  /** @polyTranslatable false */
  logo8CtaId?: string;

  /** Logo 9 */
  /** @polyType Url */
  logo9ImageUrl?: string;
  /** @polyTranslatable true */
  logo9Alt?: string;
  /** @polyType Url */
  logo9Href?: string;
  /** @polyTranslatable false */
  logo9CtaId?: string;

  /** Logo 10 */
  /** @polyType Url */
  logo10ImageUrl?: string;
  /** @polyTranslatable true */
  logo10Alt?: string;
  /** @polyType Url */
  logo10Href?: string;
  /** @polyTranslatable false */
  logo10CtaId?: string;

  /** Logo 11 */
  /** @polyType Url */
  logo11ImageUrl?: string;
  /** @polyTranslatable true */
  logo11Alt?: string;
  /** @polyType Url */
  logo11Href?: string;
  /** @polyTranslatable false */
  logo11CtaId?: string;

  /** Logo 12 */
  /** @polyType Url */
  logo12ImageUrl?: string;
  /** @polyTranslatable true */
  logo12Alt?: string;
  /** @polyType Url */
  logo12Href?: string;
  /** @polyTranslatable false */
  logo12CtaId?: string;
}

/**
 * @polySemanticUsage About this block: a banner displaying a row of partner, customer, or brand
 * logos. Use it as a trust and credibility signal — "trusted by" or "as seen in" style social
 * proof. Minimal text; the logos themselves are the content. Holds 3 to 12 logos, filled in
 * order from logo 1 with no gaps (logos 1 to 3 are required): 3 to 5 sit in a static row, 6 to
 * 12 scroll. With fewer than 3 logos, use a different block. With more than 12, keep the 12
 * most important.
 */
export interface BrandLogosBannerProps extends BrandLogosBannerSlotProps {
  /**
   * Accessible name for the banner region. Authored and translatable because
   * the banner has no heading to name itself from.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for this banner. Translated with the page.
   * @polyDefault Brand logos
   */
  ariaLabel?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const DEFAULT_ARIA_LABEL = 'Brand logos';

const SLOT_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
type SlotIndex = typeof SLOT_INDEXES[number];
type SlotField = 'ImageUrl' | 'Alt' | 'Href' | 'CtaId';

function readSlotField(
  props: BrandLogosBannerSlotProps,
  slot: SlotIndex,
  field: SlotField,
): string | undefined {
  return props[`logo${slot}${field}` as keyof BrandLogosBannerSlotProps];
}

interface ResolvedLogo {
  slot: SlotIndex;
  url: string;
  alt: string;
  href?: string;
  ctaId?: string;
  linked: boolean;
  warning?: string;
}

/**
 * Assemble a logo from a slot's flat props, applying the accessibility
 * guards: a linked logo with no alt drops its link (WCAG 2.4.4); any logo
 * with no alt renders decorative and is flagged (WCAG 1.1.1). Returns null
 * for an empty slot (no image URL).
 */
function resolveLogo(props: BrandLogosBannerSlotProps, slot: SlotIndex): ResolvedLogo | null {
  const url = readSlotField(props, slot, 'ImageUrl');
  if (!url) return null;

  const alt = readSlotField(props, slot, 'Alt');
  const href = readSlotField(props, slot, 'Href');
  const ctaId = readSlotField(props, slot, 'CtaId');

  if (href && !alt) {
    return {
      slot,
      url,
      alt: '',
      linked: false,
      warning:
        `[BrandLogosBanner] Logo slot ${slot} has logoNHref but no logoNAlt — ` +
        'dropping the link so it does not ship an anchor with no accessible name (WCAG 2.4.4). ' +
        `Add logo${slot}Alt or remove logo${slot}Href.`,
    };
  }

  if (!alt) {
    return {
      slot,
      url,
      alt: '',
      linked: false,
      warning:
        `[BrandLogosBanner] Logo slot ${slot} has no logo${slot}Alt — the image renders as ` +
        'decorative (alt=""), hiding the brand from screen readers (WCAG 1.1.1). ' +
        `Add logo${slot}Alt.`,
    };
  }

  return {
    slot,
    url,
    alt,
    href: href || undefined,
    ctaId: ctaId || undefined,
    linked: Boolean(href),
  };
}

/**
 * Decorative copies that follow the real logos in the marquee track. Each half
 * of the track repeats the set until it holds at least
 * BRAND_LOGOS_BANNER_MAX_LOGOS items, so a 6-logo set is as wide as a 12-logo
 * one and does not leave a gap before the loop restarts on wide viewports. The
 * second half is an exact copy of the first, which keeps the -50% snap seamless.
 */
function marqueeClones(logos: ResolvedLogo[]): ResolvedLogo[] {
  const trackLength = itemsPerMarqueeHalf(logos.length) * 2;
  return Array.from({ length: trackLength - logos.length }, (_, index) => logos[index % logos.length]);
}

function itemsPerMarqueeHalf(logoCount: number): number {
  return logoCount * Math.ceil(BRAND_LOGOS_BANNER_MAX_LOGOS / logoCount);
}

/**
 * The marquee duration token is the time for one 12-item half. The half grows
 * with the repeats, so the duration scales by the same factor to keep every
 * logo moving at the same speed whatever the logo count.
 */
function marqueeSpeedStyle(logoCount: number): React.CSSProperties {
  return {
    '--poly-size-brandLogosBanner-itemsPerHalf': itemsPerMarqueeHalf(logoCount),
  } as React.CSSProperties;
}

export const BrandLogosBanner: React.FC<BrandLogosBannerProps> = (props) => {
  const [paused, setPaused] = useState(false);

  const logos = SLOT_INDEXES
    .map(slot => resolveLogo(props, slot))
    .filter((logo): logo is ResolvedLogo => logo !== null);

  if (logos.length < BRAND_LOGOS_BANNER_MIN_LOGOS) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- authoring diagnostic for the CMS-empty misconfig
      console.warn(
        `[BrandLogosBanner] Requires at least ${BRAND_LOGOS_BANNER_MIN_LOGOS} logos; ` +
        `got ${logos.length}. Populate more logo{N}ImageUrl slots.`,
      );
    }
    return (
      <div
        data-poly-error="brand-logos-banner-insufficient"
        data-poly-error-detail={`Needs ${BRAND_LOGOS_BANNER_MIN_LOGOS} logos, got ${logos.length}`}
        aria-hidden="true"
        hidden
      />
    );
  }

  if (process.env.NODE_ENV !== 'production') {
    logos.forEach(logo => {
      if (logo.warning) {
        // eslint-disable-next-line no-console -- authoring accessibility diagnostic
        console.warn(logo.warning);
      }
    });
  }

  const renderLogo = (logo: ResolvedLogo, key: string, isClone: boolean) => {
    const image = (
      <Image
        bare
        url={logo.url}
        alt={isClone ? '' : logo.alt}
        className="brand-logos-banner__logo"
        loading="lazy"
      />
    );

    return (
      <li
        key={key}
        className={`brand-logos-banner__slot${isClone ? ' brand-logos-banner__slot--clone' : ''}`}
        aria-hidden={isClone || undefined}
      >
        {!isClone && logo.linked ? (
          <a className="brand-logos-banner__link" href={logo.href} data-cta-id={logo.ctaId}>
            {image}
          </a>
        ) : (
          image
        )}
      </li>
    );
  };

  const isScrolling = logos.length >= BRAND_LOGOS_BANNER_MIN_SCROLLING_LOGOS;
  const realSlots = logos.map((logo, index) => renderLogo(logo, `logo-${index}`, false));
  const cloneSlots = isScrolling
    ? marqueeClones(logos).map((logo, index) => renderLogo(logo, `clone-${index}`, true))
    : [];
  const modifiers = [
    isScrolling ? '' : ' brand-logos-banner--static',
    paused ? ' brand-logos-banner--paused' : '',
  ].join('');

  return (
    <section
      {...blockAnchorAttributes(props.anchorId)}
      className={`brand-logos-banner${modifiers}`}
      style={isScrolling ? marqueeSpeedStyle(logos.length) : undefined}
      aria-label={props.ariaLabel || DEFAULT_ARIA_LABEL}
    >
      <div className="brand-logos-banner__inner">
        <ul className="brand-logos-banner__row" role="list">
          {realSlots}
          {cloneSlots}
        </ul>
      </div>
      {isScrolling && (
        <button
          type="button"
          className="brand-logos-banner__pause"
          aria-label={paused ? 'Play logo animation' : 'Pause logo animation'}
          onClick={() => setPaused((prev) => !prev)}
        >
          <CustomIcon name={paused ? 'play' : 'pause'} size={16} aria-hidden="true" />
        </button>
      )}
    </section>
  );
};
